import type { ReactNode, Ref } from "react";
import { graphql, useFragment } from "react-relay";

import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { AdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import type { topStoriesBlockQueueItemsEdge$key } from "scmp-app/queries/__generated__/topStoriesBlockQueueItemsEdge.graphql";

import {
  AdsSlotContainer,
  Container,
  LeftContainer,
  MLrecAdContainer,
  RightContainer,
  StyledTopStoriesItem,
} from "./styles";

type Props = {
  adZone?: string;
  className?: string;
  isReadingTimeEnabled?: boolean;
  isShowAds?: boolean;
  isShowMLrecAd?: boolean;
  onClick?: (
    entityId?: string,
    urlAlias?: string,
    isSponsorArticle?: boolean,
    index?: number,
  ) => void;
  ref?: Ref<HTMLDivElement>;
  reference: topStoriesBlockQueueItemsEdge$key;
  slots?: Slots;
  withBookmark?: boolean;
  withCaption?: boolean;
  withSeriesShortLabel?: boolean;
  withTopic?: boolean;
};

type Slots = {
  LastItemAd?: ReactNode;
  MLrecAd?: ReactNode;
};

export const TopStoriesBlock = ({
  adZone,
  className,
  isReadingTimeEnabled = false,
  isShowAds = false,
  isShowMLrecAd = false,
  onClick,
  ref,
  reference: reference_,
  slots,
  withBookmark = false,
  withCaption = true,
  withSeriesShortLabel = false,
  withTopic = false,
}: Props) => {
  const items = useFragment(
    graphql`
      fragment topStoriesBlockQueueItemsEdge on QueueItemsEdge @relay(plural: true) {
        node {
          ... on Content {
            ...topStoriesItemContent
          }
        }
      }
    `,
    reference_,
  );

  const renderMLrecAd = () => {
    if (!isShowMLrecAd) return null;
    if (slots?.MLrecAd) return <MLrecAdContainer>{slots.MLrecAd}</MLrecAdContainer>;
    return (
      <MLrecAdContainer>
        <AdSlot
          adUnit="m_lrec1"
          breakpoint={theme.breakpoints.only("mobile")}
          sizes={[[300, 250]]}
          targeting={{
            paid: TargetingPaidType.NotArticle,
          }}
          zone={adZone}
        />
      </MLrecAdContainer>
    );
  };

  const renderLastItemAd = () => {
    if (!isShowAds) return null;
    if (slots?.LastItemAd) return <AdsSlotContainer>{slots.LastItemAd}</AdsSlotContainer>;
    return (
      <AdsSlotContainer>
        <AdSlot
          adUnit="d_native2c"
          breakpoint={theme.breakpoints.up("desktop")}
          sizes={[[1, 1], "fluid"]}
          targeting={{
            paid: TargetingPaidType.NotArticle,
          }}
          zone={adZone}
        />
        <AdSlot
          adUnit="m_native2c"
          breakpoint={theme.breakpoints.down("desktop")}
          sizes={[[1, 1], "fluid"]}
          targeting={{
            paid: TargetingPaidType.NotArticle,
          }}
          zone={adZone}
        />
      </AdsSlotContainer>
    );
  };

  if (!items) return null;

  const [first, second, ...rest] = items.filter(notEmpty).map(({ node }) => node);

  return (
    <Container className={className} ref={ref}>
      <LeftContainer $isShowMLrecAd={isShowMLrecAd}>
        {first && (
          <StyledTopStoriesItem
            isReadingTimeEnabled={isReadingTimeEnabled}
            onClick={(entityId, urlAlias, isSponsorArticle) => {
              onClick?.(entityId, urlAlias, isSponsorArticle, 0);
            }}
            reference={first}
            topicLinkVariant={{ type: "main" }}
            variant="primary"
            withBookmark={withBookmark}
            withCaption={withCaption}
            withSeriesShortLabel={withSeriesShortLabel}
            withTopic={withTopic}
          />
        )}
        {renderMLrecAd()}
        {second && (
          <StyledTopStoriesItem
            isReadingTimeEnabled={isReadingTimeEnabled}
            onClick={(entityId, urlAlias, isSponsorArticle) => {
              onClick?.(entityId, urlAlias, isSponsorArticle, 1);
            }}
            reference={second}
            topicLinkVariant={{ type: "main" }}
            variant="secondary"
            withBookmark={withBookmark}
            withSeriesShortLabel={withSeriesShortLabel}
            withTopic={withTopic}
          />
        )}
      </LeftContainer>
      <RightContainer>
        {rest.map((item, itemIndex) => (
          <StyledTopStoriesItem
            isReadingTimeEnabled={isReadingTimeEnabled}
            isShowImage={!isShowAds && itemIndex === rest.length - 1}
            key={itemIndex}
            onClick={(entityId, urlAlias, isSponsorArticle) => {
              onClick?.(entityId, urlAlias, isSponsorArticle, itemIndex + 2);
            }}
            reference={item}
            topicLinkVariant={{ type: "main" }}
            variant="secondary"
            withBookmark={withBookmark}
            withSeriesShortLabel={withSeriesShortLabel}
            withTopic={withTopic}
          />
        ))}
        {renderLastItemAd()}
      </RightContainer>
    </Container>
  );
};

TopStoriesBlock.displayName = "TopStoriesBlock";
